import { WEB_LIGHT_THEME, renderCssVariables, renderEffectsCss, cssVariablesFor, type Theme } from '@vello/tokens';

/** The theme token set to render (defaults to the light theme — the Vello Claude Design's default surface). */
type ThemeTokens = Parameters<typeof renderCssVariables>[0];

/**
 * The stylesheet text of the Vello design system's runtime layer: the `@vello/tokens` custom properties on `:root`
 * plus the effect layer (FX-01…16). `installVelloTheme()` puts this string in a `<style id="vello-theme">` at boot;
 * a surface that is not a React app (the Astro manual, built statically) injects the same string at build time.
 * Importing this file pulls in nothing but `@vello/tokens`, so Node and a static-site config can load it directly.
 *
 * D798: with a `dark` theme the same sheet also carries the dark surface — the custom properties that differ from the light
 * ones — under `<html data-theme="dark">`, which only the console shell sets (`theme-preference.ts`). The sidebar takes the
 * dark palette's inverse surface as it comes: snow, with the navy `on-inverse` ramp on it — light rail, dark page, the
 * reverse of the light theme's navy rail on a light page (owner, 2026-10-28).
 */
export function velloThemeCss(theme: ThemeTokens = WEB_LIGHT_THEME, dark?: Theme): string {
  const base = `:root {\n${renderCssVariables(theme)}\n}\n`;
  return dark === undefined ? `${base}${renderEffectsCss()}` : `${base}${darkThemeCss(theme, dark)}\n${renderEffectsCss()}`;
}

/** The variables of `dark` whose value differs from `light`'s, as rule-body lines. */
function changedVariables(light: ThemeTokens, dark: Theme): string {
  const base = cssVariablesFor(light);
  return Object.entries(cssVariablesFor(dark))
    .filter(([k, v]) => base[k] !== v)
    .map(([k, v]) => `  ${k}: ${v};`)
    .join('\n');
}

function darkThemeCss(light: ThemeTokens, dark: Theme): string {
  return `:root[data-theme="dark"] {\n  color-scheme: dark;\n${changedVariables(light, dark)}\n}`;
}
